import type { JobPostFormInput } from "@/components/Forms/JobPostForm/types";
import { isFacilityWebsiteApplyVia } from "./copy";
import type { EstimateJobApplicantCountPayload, JobApplicantCountEstimateInput } from "./mutations";
import type { JobApplicantCountEstimate } from "./queries";

export type EstimateGeography = {
  state?: string;
  sectionName?: string;
};

export type FacilityEstimateGeography = {
  mainAddress?: { state?: string | null } | null;
  mailingAddress?: { state?: string | null } | null;
  section?: { name?: string | null } | null;
} | null;

function presentText(value?: string | null): boolean {
  return Boolean(value && value.trim() !== "");
}

function presentCompensationCents(value?: number): value is number {
  return typeof value === "number" && !Number.isNaN(value) && value > 0;
}

function centsToDollars(cents?: number): number | undefined {
  if (!presentCompensationCents(cents)) {
    return undefined;
  }

  return cents / 100;
}

export function geographyFromFacility(facility?: FacilityEstimateGeography): EstimateGeography {
  const state = facility?.mainAddress?.state?.trim() || facility?.mailingAddress?.state?.trim();
  if (state) {
    return { state };
  }

  const sectionName = facility?.section?.name?.trim();
  if (sectionName) {
    return { sectionName };
  }

  return {};
}

export function hasEstimateGeography(geography: EstimateGeography): boolean {
  return presentText(geography.state) || presentText(geography.sectionName);
}

export function isEstimateCriteriaComplete(
  form: Pick<
    JobPostFormInput,
    | "serviceLevel"
    | "category"
    | "compensation"
    | "applicantGroup"
    | "positionType"
    | "requiredExperienceInYears"
    | "paySchedule"
    | "requiredEducation"
    | "additionalPgaEducation"
  >,
  geography: EstimateGeography,
  options?: { geographyLoading?: boolean }
): boolean {
  if (options?.geographyLoading) {
    return false;
  }

  const experienceYears = Number(form.requiredExperienceInYears);

  return (
    presentText(form.serviceLevel) &&
    presentText(form.category) &&
    hasEstimateGeography(geography) &&
    (presentCompensationCents(form.compensation?.baseMinimum) ||
      presentCompensationCents(form.compensation?.totalAnticipated)) &&
    presentText(form.applicantGroup) &&
    presentText(form.positionType) &&
    form.requiredExperienceInYears !== undefined &&
    form.requiredExperienceInYears !== "" &&
    !Number.isNaN(experienceYears) &&
    presentText(form.paySchedule) &&
    presentText(form.requiredEducation) &&
    presentText(form.additionalPgaEducation)
  );
}

export function buildEstimateInputFromForm(
  form: JobPostFormInput,
  geography: EstimateGeography,
  options?: { geographyLoading?: boolean }
): JobApplicantCountEstimateInput | null {
  if (!isEstimateCriteriaComplete(form, geography, options)) {
    return null;
  }

  const compensation: JobApplicantCountEstimateInput["compensation"] = {};
  const baseMinimum = centsToDollars(form.compensation?.baseMinimum);
  const baseMaximum = centsToDollars(form.compensation?.baseMaximum);
  const totalAnticipated = centsToDollars(form.compensation?.totalAnticipated);

  if (baseMinimum !== undefined) {
    compensation.baseMinimum = baseMinimum;
  }
  if (baseMaximum !== undefined) {
    compensation.baseMaximum = baseMaximum;
  }
  if (totalAnticipated !== undefined) {
    compensation.totalAnticipated = totalAnticipated;
  }
  if (form.compensation?.currencyUnit) {
    compensation.currencyUnit = form.compensation.currencyUnit;
  }

  const input: JobApplicantCountEstimateInput = {
    serviceLevel: form.serviceLevel.trim(),
    category: form.category.trim(),
    applicantGroup: form.applicantGroup.trim(),
    positionType: form.positionType.trim(),
    paySchedule: form.paySchedule.trim(),
    requiredEducation: form.requiredEducation.trim(),
    additionalPgaEducation: form.additionalPgaEducation.trim(),
    requiredExperienceInYears: Number(form.requiredExperienceInYears)
  };

  if (geography.state) {
    input.state = geography.state;
  }
  if (geography.sectionName) {
    input.sectionName = geography.sectionName;
  }
  if (Object.keys(compensation).length > 0) {
    input.compensation = compensation;
  }
  // Facility Website posts still receive a PGA.org range; the block shows the disclaimer.
  if (form.applyVia && !isFacilityWebsiteApplyVia(form.applyVia)) {
    input.applyVia = form.applyVia;
  }

  return input;
}

export function toRangedEstimate(payload?: EstimateJobApplicantCountPayload | null): JobApplicantCountEstimate | null {
  if (!payload?.criteriaComplete) {
    return null;
  }
  if (
    payload.low == null ||
    payload.high == null ||
    payload.median == null ||
    payload.sampleSize == null ||
    payload.matchTier == null
  ) {
    return null;
  }

  return {
    low: payload.low,
    high: payload.high,
    median: payload.median,
    sampleSize: payload.sampleSize,
    matchTier: payload.matchTier,
    estimatorVersion: payload.estimatorVersion,
    disclaimer: payload.disclaimer,
    criteriaComplete: true,
    missingCriteria: payload.missingCriteria
  };
}
